import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import moment from 'moment';
import DateTimePicker from 'react-datetime';
import noUiSlider from 'nouislider';
import 'nouislider/dist/nouislider.css';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  TimeScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import 'chartjs-adapter-moment';

import { GlobalStore } from '../../store';
import { loadQueues } from '../../actions/queueActions';
import { resetTitle } from '../../actions/titleActions';
import { downloadFile, copyToClipboard, addFlashCard } from '../../utils/UtilityFunctions';
import PageNotFound from '../NoMatch';
import ErrorMessage from '../../viewcomponents/ErrorMessage';
import User from '../../models/User';
import { Copy, Download } from '../../viewcomponents/FontAwesome';
import { HTTP_SERVER_URL } from '../../configuration';

ChartJS.register(CategoryScale, LinearScale, TimeScale, BarElement, LineElement, PointElement, Tooltip, Legend);

// White background plugin — PNG downloads get white bg instead of transparent
ChartJS.register({
  id: 'whiteBg',
  beforeDraw(chart: any) {
    const ctx = chart.canvas.getContext('2d');
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, chart.width, chart.height);
    ctx.restore();
  },
});

// ─── Types ───────────────────────────────────────────────────────────

interface SessionSummary {
  session_key: number;
  date: string;
  lab_start: string;
  lab_end: string | null;
  duration_min: number | null;
  total_helped: number;
  total_presented: number;
  avg_wait_h1_secs: number | null;
  avg_wait_h2_secs: number | null;
  max_wait_secs: number | null;
  remaining_at_end: number;
}

interface ChartPoint {
  minutes_from_start: number;
  queue_length: number;
  help_amount: number;
  present_amount: number;
}

interface HelpSpan {
  started_min: number;
  ended_min: number | null;
}

interface SessionDetail {
  points: ChartPoint[];
  help_spans: HelpSpan[];
}

interface HourlySlot {
  hour: number;
  queued_help: number;
  queued_present: number;
  assisted_help: number;
  assisted_present: number;
  avg_wait_service_secs: number | null;
  max_wait_service_secs: number | null;
  avg_wait_arrival_secs: number | null;
  max_wait_arrival_secs: number | null;
  remaining: number;
}

interface RawHelpEvent {
  helper_username: string;
  helper_name: string;
  started_at: string;
  ended_at: string | null;
  help: boolean;
}

interface DateGroup {
  date: string;
  sessions: SessionSummary[];
  total_helped: number;
  total_presented: number;
  avg_wait_secs: number | null;
  max_wait_secs: number | null;
  primary: SessionSummary;
}

// ─── Helpers ─────────────────────────────────────────────────────────

const snapDown5 = (ms: number) => Math.floor(ms / (5 * 60_000)) * (5 * 60_000);
const snapUp5   = (ms: number) => Math.ceil( ms / (5 * 60_000)) * (5 * 60_000);

function fmtWait(secs: number | null): string {
  if (secs === null) return '—';
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

function fmtDate(isoDate: string): string {
  const d = new Date(isoDate + 'T12:00:00Z');
  const parts = new Intl.DateTimeFormat('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC',
  }).formatToParts(d);
  const get = (t: string) => parts.find(p => p.type === t)?.value ?? '';
  return `${get('weekday')} ${get('day')} ${get('month')}`;
}

function stockholmHour(date: Date): number {
  return parseInt(
    date.toLocaleTimeString('sv-SE', { hour: '2-digit', hour12: false, timeZone: 'Europe/Stockholm' })
  );
}

function toIsoDate(unixSecs: number): string {
  return new Date(unixSecs * 1000).toLocaleDateString('sv-SE');
}

function buildIsoParams(from: number | null, until: number | null): string {
  const parts: string[] = [];
  if (from !== null) parts.push(`from=${toIsoDate(from)}`);
  if (until !== null) parts.push(`until=${toIsoDate(until)}`);
  return parts.length > 0 ? '?' + parts.join('&') : '';
}

function queueApiBase(queueName: string): string {
  return `${HTTP_SERVER_URL}/api/queues/${encodeURIComponent(queueName)}`;
}

function groupByDate(sessions: SessionSummary[]): DateGroup[] {
  const map = new Map<string, SessionSummary[]>();
  for (const s of sessions) {
    const list = map.get(s.date) ?? [];
    list.push(s);
    map.set(s.date, list);
  }
  return Array.from(map.entries()).map(([date, ss]) => {
    const total_helped = ss.reduce((a, s) => a + s.total_helped, 0);
    const total_presented = ss.reduce((a, s) => a + s.total_presented, 0);
    const waits = ss
      .flatMap(s => [s.avg_wait_h1_secs, s.avg_wait_h2_secs])
      .filter((w): w is number => w !== null);
    const avg_wait_secs = waits.length > 0
      ? waits.reduce((a, b) => a + b, 0) / waits.length
      : null;
    const maxes = ss.map(s => s.max_wait_secs).filter((w): w is number => w !== null);
    const max_wait_secs = maxes.length > 0 ? Math.max(...maxes) : null;
    // primary = earliest session (used as the time reference for merged charts)
    const primary = ss.reduce((earliest, s) =>
      new Date(s.lab_start) < new Date(earliest.lab_start) ? s : earliest
    );
    return {
      date, sessions: ss, total_helped, total_presented,
      avg_wait_secs, max_wait_secs, primary,
    };
  });
}

function buildStackedBuckets(
  detail: SessionDetail,
  labStart: string,
  hourFrom: number,
  hourTo: number,
): [Date, number, number][] {
  const t0 = new Date(labStart).getTime();
  const points = detail.points;
  const spans = detail.help_spans;
  if (points.length === 0) return [];
  const lastMin = points[points.length - 1].minutes_from_start;
  const rows: [Date, number, number][] = [];
  for (let m = 0; m <= lastMin + 5; m += 5) {
    const t = new Date(t0 + m * 60_000);
    const h = stockholmHour(t);
    if (h < hourFrom || h > hourTo) continue;
    const candidates = points.filter(p => p.minutes_from_start <= m);
    if (candidates.length === 0) continue;
    const p = candidates[candidates.length - 1];
    const assisted = spans.filter(s =>
      s.started_min <= m && (s.ended_min === null || s.ended_min >= m)
    ).length;
    const waiting = Math.max(0, p.help_amount + p.present_amount - assisted);
    rows.push([t, assisted, waiting]);
  }
  return rows;
}

function downloadPng(chartRef: React.RefObject<any>, filename: string) {
  const chart = chartRef.current;
  if (!chart) return;
  const a = document.createElement('a');
  a.href = chart.toBase64Image('image/png', 1);
  a.download = filename + '.png';
  a.click();
}

// ─── Styles ──────────────────────────────────────────────────────────

const sliderStyles = `
.noUi-connect { background: #00695C; }
.noUi-handle { border-color: #00695C; }
.noUi-handle:after, .noUi-handle:before { background: #00695C; }
.noUi-tooltip {
  font-size: 0.72rem; padding: 1px 4px; bottom: 125%;
  border-color: #adb5bd; color: #495057;
}
.noUi-pips-horizontal { height: 28px; padding: 4px 0 0; }
.noUi-value-horizontal { font-size: 0.68rem; color: #6c757d; }
.noUi-marker-horizontal.noUi-marker { height: 6px; }
.hour-table th, .hour-table td { font-size: 0.82rem; }
.hour-table tr.dim td { color: #bbb; }
.hour-table th.group-h { text-align: center; background: #e9ecef; border-bottom: 0; }
.hour-table th.sub-h { text-align: center; font-weight: normal; color: #6c757d; }
.hour-table td:not(:first-child) { text-align: center; }
.option-box {
  border: 1px solid #dee2e6; border-radius: 6px;
  padding: 0.6rem 0.9rem; margin-bottom: 0.9rem; background: #fff;
}
.option-box label { margin-right: 1rem; cursor: pointer; font-weight: normal; margin-bottom: 0; }
.opt-label { font-weight: 600; margin-right: 0.5rem; color: #495057; }
.chart-wrap { margin-bottom: 1.5rem; }
.chart-header {
  display: flex; align-items: baseline;
  justify-content: space-between; margin-bottom: 0.25rem;
}
.btn-dl {
  font-size: 0.72rem; padding: 1px 7px; line-height: 1.6;
  color: #6c757d; background: #fff; border: 1px solid #ced4da;
  border-radius: 3px; cursor: pointer; opacity: 0; transition: opacity 0.15s;
}
.chart-wrap:hover .btn-dl { opacity: 1; }
.btn-dl:hover { background: #f8f9fa; color: #343a40; }
.section-divider { border-top: 2px solid #dee2e6; margin: 1.4rem 0 1rem; }
`;

// ─── Component ───────────────────────────────────────────────────────

export default (): JSX.Element => {
  const user = useSelector<GlobalStore, User | null>(store => store.user);
  const queues = useSelector<GlobalStore, string[]>(store =>
    store.queues.queueList.map(q => q.name)
  ).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

  const [errorMessage, setErrorMessage] = useState('');
  const ninetyDaysAgo = new Date();
  ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
  const [from, setFrom] = useState<number | null>(Math.round(ninetyDaysAgo.getTime() / 1000));
  const [until, setUntil] = useState<number | null>(Math.round(new Date().getTime() / 1000));
  const lastVisited = localStorage.getItem('lastVisitedQueue');
  const [selectedQueue, setSelectedQueue] = useState<string | null>(
    lastVisited && queues.includes(lastVisited) ? lastVisited : null
  );
  const [queueSearch, setQueueSearch] = useState(selectedQueue || '');

  const [sessions, setSessions] = useState<SessionSummary[] | null>(null);
  const [minDurationMin, setMinDurationMin] = useState<number>(15);
  const [selectedDateKey, setSelectedDateKey] = useState<string | null>(null);
  const [sessionDetail, setSessionDetail] = useState<SessionDetail | null>(null);
  const [hourlySlots, setHourlySlots] = useState<HourlySlot[] | null>(null);
  const [helpEventsRaw, setHelpEventsRaw] = useState<RawHelpEvent[] | null>(null);
  const [queueLog, setQueueLog] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'sessions' | 'queue_log'>('sessions');
  const [sessionsSort, setSessionsSort] = useState<'asc' | 'desc'>('desc');
  const [hourFrom, setHourFrom] = useState<number>(0);
  const [hourTo, setHourTo] = useState<number>(23);
  const [waitMode, setWaitMode] = useState<'service' | 'arrival'>('service');
  const [alignAxes, setAlignAxes] = useState(false);

  // Refs
  const sliderRef = useRef<HTMLDivElement>(null);
  const sliderInstance = useRef<any>(null);
  const sliderUpdating = useRef(false);
  const queueLoadRef = useRef<any>(null);
  const fiveMinRef = useRef<any>(null);
  const taChartRef = useRef<any>(null);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!dispatch) return;
    dispatch(loadQueues());
    dispatch(resetTitle());
  }, [dispatch]);

  // Init noUiSlider when detail section first renders
  useEffect(() => {
    if (!selectedDateKey || !sliderRef.current) return;
    if (sliderInstance.current) return; // already initialized

    const slider = noUiSlider.create(sliderRef.current, {
      start: [hourFrom, hourTo],
      connect: true,
      range: { min: 0, max: 23 },
      step: 1,
      tooltips: [
        { to: (v: number) => Math.round(v) + ':00' },
        { to: (v: number) => Math.round(v) + ':59' },
      ],
      pips: {
        mode: 'values' as any,
        values: [0, 3, 6, 9, 12, 15, 18, 21, 23],
        density: 4,
        format: { to: (v: number) => v + 'h', from: Number },
      },
    });

    slider.on('change', (values: (string | number)[]) => {
      if (sliderUpdating.current) return;
      setHourFrom(Math.round(parseFloat(values[0] as string)));
      setHourTo(Math.round(parseFloat(values[1] as string)));
    });

    sliderInstance.current = slider;
  }, [selectedDateKey]);

  // Sync slider position when hourFrom/hourTo change (from inputs or auto-range)
  useEffect(() => {
    if (!sliderInstance.current) return;
    sliderUpdating.current = true;
    sliderInstance.current.set([hourFrom, hourTo]);
    sliderUpdating.current = false;
  }, [hourFrom, hourTo]);

  // Cleanup slider on unmount
  useEffect(() => {
    return () => {
      if (sliderInstance.current) {
        sliderInstance.current.destroy();
        sliderInstance.current = null;
      }
    };
  }, []);

  // ─── Date pickers ───────────────────────────────────────────

  function updateFrom(value: moment.Moment | string): void {
    try {
      const unix = (value as moment.Moment).unix();
      setFrom(unix);
      setErrorMessage(until !== null && unix > until ? 'From-date must be before until-date' : '');
    } catch {
      setFrom(null);
    }
  }

  function updateUntil(value: moment.Moment | string): void {
    try {
      const unix = (value as moment.Moment).unix();
      setUntil(unix);
      setErrorMessage(from !== null && unix < from ? 'From-date must be before until-date' : '');
    } catch {
      setUntil(null);
    }
  }

  function isValidFromDate(t: moment.Moment): boolean {
    return (
      t.isAfter(moment().subtract(5, 'year')) &&
      (until === null || t.isBefore(moment.unix(until))) &&
      t.isBefore(moment())
    );
  }

  function isValidUntilDate(t: moment.Moment): boolean {
    return (
      t.isAfter(moment().subtract(5, 'year')) &&
      (from === null || t.isAfter(moment.unix(from))) &&
      t.isBefore(moment())
    );
  }

  // ─── Data loading ───────────────────────────────────────────

  async function loadData() {
    if (!user || !selectedQueue || errorMessage) return;
    if (sliderInstance.current) {
      sliderInstance.current.destroy();
      sliderInstance.current = null;
    }
    setLoading(true);
    setSessions(null);
    setSelectedDateKey(null);
    setSessionDetail(null);
    setHourlySlots(null);
    setHelpEventsRaw(null);
    setQueueLog(null);

    const headers = { Authorization: `Token ${user.token}` };
    const params = buildIsoParams(from, until);
    const base = queueApiBase(selectedQueue);

    try {
      const resp = await axios.get<SessionSummary[]>(
        `${base}/statistics/sessions${params}`,
        { headers }
      );
      setSessions(resp.data);
    } catch (e) {
      setErrorMessage('Failed to load statistics');
    }

    setLoading(false);
  }

  async function loadDateDetail(group: DateGroup) {
    if (!user || !selectedQueue) return;
    setSelectedDateKey(group.date);
    setSessionDetail(null);
    setHourlySlots(null);
    setHelpEventsRaw(null);

    const headers = { Authorization: `Token ${user.token}` };
    const base = queueApiBase(selectedQueue);

    // Fetch detail + hourly for every session in the group, plus the day's interaction log
    const sessionFetches = group.sessions.map(s =>
      Promise.all([
        axios.get<SessionDetail>(`${base}/statistics/sessions/${s.session_key}`, { headers }).catch(() => null),
        axios.get<HourlySlot[]>(`${base}/statistics/sessions/${s.session_key}/hourly`, { headers }).catch(() => null),
      ])
    );
    const [sessionResults, eventsResp] = await Promise.all([
      Promise.all(sessionFetches),
      axios.get<RawHelpEvent[]>(`${base}/interaction_log?from=${group.date}&until=${group.date}`, { headers }).catch(() => null),
    ]);

    // Merge session details: re-offset all points relative to group.primary.lab_start (earliest)
    const t0 = new Date(group.primary.lab_start).getTime();
    const mergedPoints: ChartPoint[] = [];
    const mergedSpans: HelpSpan[] = [];
    for (let i = 0; i < group.sessions.length; i++) {
      const s = group.sessions[i];
      const detail = sessionResults[i][0]?.data;
      if (!detail) continue;
      const offset = Math.round((new Date(s.lab_start).getTime() - t0) / 60_000);
      for (const p of detail.points) {
        mergedPoints.push({ ...p, minutes_from_start: p.minutes_from_start + offset });
      }
      for (const sp of detail.help_spans) {
        mergedSpans.push({
          started_min: sp.started_min + offset,
          ended_min: sp.ended_min !== null ? sp.ended_min + offset : null,
        });
      }
    }
    mergedPoints.sort((a, b) => a.minutes_from_start - b.minutes_from_start);
    mergedSpans.sort((a, b) => a.started_min - b.started_min);

    // Merge hourly slots: concatenate across sessions, sort by hour
    const mergedHourly: HourlySlot[] = sessionResults
      .flatMap(([, hourlyResp]) => hourlyResp?.data ?? [])
      .sort((a, b) => a.hour - b.hour);

    setSessionDetail({ points: mergedPoints, help_spans: mergedSpans });
    setHourlySlots(mergedHourly);
    setHelpEventsRaw(eventsResp?.data ?? []);

    // Auto-set slider to span first event of first session through last event of last session
    if (mergedPoints.length > 0) {
      const firstH = stockholmHour(new Date(t0 + mergedPoints[0].minutes_from_start * 60_000));
      const lastH = stockholmHour(new Date(t0 + mergedPoints[mergedPoints.length - 1].minutes_from_start * 60_000));
      setHourFrom(firstH);
      setHourTo(lastH);
    }
  }

  async function loadQueueLog() {
    if (!user || !selectedQueue) return;
    const isoParams = buildIsoParams(from, until);
    try {
      const resp = await axios.get(
        `${queueApiBase(selectedQueue)}/queue_log${isoParams}`,
        { headers: { Authorization: `Token ${user.token}` } }
      );
      setQueueLog(JSON.stringify(resp.data, null, 2));
    } catch (e) {
      setQueueLog(e.toString());
    }
  }

  // ─── Computed data ──────────────────────────────────────────

  const filteredQueues = queues.filter(q => q.toLowerCase().includes(queueSearch.toLowerCase()));

  const allGroups = sessions ? groupByDate(sessions) : [];
  const filteredGroups = allGroups.filter(g =>
    g.sessions.every(s => s.duration_min === null || s.duration_min >= minDurationMin)
    || g.sessions.some(s => s.duration_min !== null && s.duration_min >= minDurationMin)
  );
  const hiddenCount = sessions ? sessions.length - filteredGroups.reduce((a, g) => a + g.sessions.length, 0) : 0;

  const selectedGroup = filteredGroups.find(g => g.date === selectedDateKey) ?? null;

  // Filter hourly slots by current hour range
  const filteredHourly = (hourlySlots ?? []).filter(
    s => s.hour >= hourFrom && s.hour <= hourTo
  );

  // Aggregate TAs from raw help events, filtered by hour range
  const taData = React.useMemo(() => {
    if (!helpEventsRaw) return [];
    const map = new Map<string, { realname: string; helped: number; presented: number }>();
    for (const ev of helpEventsRaw) {
      const h = stockholmHour(new Date(ev.started_at));
      if (h < hourFrom || h > hourTo) continue;
      if (!map.has(ev.helper_username)) {
        map.set(ev.helper_username, { realname: ev.helper_name, helped: 0, presented: 0 });
      }
      const entry = map.get(ev.helper_username)!;
      if (ev.help) entry.helped++; else entry.presented++;
    }
    return Array.from(map.entries())
      .map(([username, { realname, helped, presented }]) => ({ username, realname, helped, presented }))
      .sort((a, b) => (b.helped + b.presented) - (a.helped + a.presented));
  }, [helpEventsRaw, hourFrom, hourTo]);

  // ─── CSV download ──────────────────────────────────────────

  function downloadHourlyCsv() {
    const headers = 'Hour,Queued Help,Queued Presentation,Assisted Help,Assisted Presentation,Avg Wait (s),Max Wait (s),Remaining';
    const rows = filteredHourly.map(s => {
      const avg = waitMode === 'service' ? s.avg_wait_service_secs : s.avg_wait_arrival_secs;
      const max = waitMode === 'service' ? s.max_wait_service_secs : s.max_wait_arrival_secs;
      return [
        `${s.hour}:00-${s.hour}:59`,
        s.queued_help, s.queued_present,
        s.assisted_help, s.assisted_present,
        avg !== null ? Math.round(avg) : '',
        max !== null ? max : '',
        s.remaining,
      ].join(',');
    });
    downloadFile(
      `hourly-${selectedQueue}-${selectedDateKey}.csv`,
      [headers, ...rows].join('\n')
    );
  }

  // ─── Render ─────────────────────────────────────────────────

  // Chart data helpers — must be computed before any early returns (Rules of Hooks)
  const chartPoints = React.useMemo(() => {
    if (!sessionDetail || !selectedGroup) return [];
    const t0 = new Date(selectedGroup.primary.lab_start).getTime();
    return sessionDetail.points.filter(p => {
      const h = stockholmHour(new Date(t0 + p.minutes_from_start * 60_000));
      return h >= hourFrom && h <= hourTo;
    });
  }, [sessionDetail, selectedGroup, hourFrom, hourTo]);

  const stackedBuckets = React.useMemo(() => {
    if (!sessionDetail || !selectedGroup) return [];
    return buildStackedBuckets(sessionDetail, selectedGroup.primary.lab_start, hourFrom, hourTo);
  }, [sessionDetail, selectedGroup, hourFrom, hourTo]);

  const sharedAxisRange = React.useMemo(() => {
    if (!alignAxes || !selectedGroup) return null;
    const t0 = new Date(selectedGroup.primary.lab_start).getTime();
    const cpTimes = chartPoints.map(p => t0 + p.minutes_from_start * 60_000);
    const sbTimes = stackedBuckets.map(([t]) => t.getTime());
    const allTimes = [...cpTimes, ...sbTimes];
    if (allTimes.length === 0) return null;
    return {
      min: new Date(snapDown5(Math.min(...allTimes))),
      max: new Date(snapUp5(Math.max(...allTimes))),
    };
  }, [alignAxes, chartPoints, stackedBuckets, selectedGroup]);

  if (user === null) return <PageNotFound />;

  return (
    <div className="container">
      <style>{sliderStyles}</style>
      <ErrorMessage message={errorMessage} />

      {/* Queue selector */}
      <div className="row mb-3">
        {user.isAdministrator ? (
          <div className="col-lg-4 pl-0" style={{ position: 'relative' }}>
            <input
              type="text"
              className="form-control"
              placeholder="Search queue..."
              value={queueSearch}
              onChange={e => setQueueSearch(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  const match = queues.find(q => q.toLowerCase().startsWith(queueSearch.toLowerCase()));
                  if (match) { setSelectedQueue(match); setQueueSearch(match); setSessions(null); }
                }
              }}
            />
            {queueSearch.length > 0 && selectedQueue !== queueSearch && filteredQueues.length > 0 && (
              <div style={{
                position: 'absolute', zIndex: 1000, background: 'white',
                border: '1px solid #ccc', borderRadius: '4px', width: '100%',
                maxHeight: '260px', overflowY: 'auto', boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
              }}>
                {filteredQueues.map(q => (
                  <button
                    key={q}
                    className="dropdown-item"
                    type="button"
                    onClick={() => { setSelectedQueue(q); setQueueSearch(q); setSessions(null); }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
            {selectedQueue && (
              <small className="text-muted">Selected: <strong>{selectedQueue}</strong></small>
            )}
          </div>
        ) : (
          <div className="col-12 pl-0" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {user.teacherIn.map(q => (
              <button
                key={q}
                type="button"
                className={`btn btn-sm ${selectedQueue === q ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => { setSelectedQueue(q); setSessions(null); }}
              >
                {q}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Date range */}
      <div className="row">
        <div className="col-lg-6 mb-3 pl-0">
          <small className="text-muted font-weight-bold">From</small>
          <DateTimePicker
            isValidDate={isValidFromDate}
            defaultValue={new Date((from as number) * 1000)}
            inputProps={{ placeholder: 'From' }}
            dateFormat="YYYY-MM-DD"
            timeFormat="HH:mm"
            onChange={updateFrom}
          />
        </div>
        <div className="col-lg-6 mb-3 pl-0">
          <small className="text-muted font-weight-bold">Until</small>
          <DateTimePicker
            isValidDate={isValidUntilDate}
            defaultValue={new Date((until as number) * 1000)}
            inputProps={{ placeholder: 'Until' }}
            dateFormat="YYYY-MM-DD"
            timeFormat="HH:mm"
            onChange={updateUntil}
          />
        </div>
      </div>

      {/* Load button */}
      <div className="row mb-4">
        <div
          className={`text-white px-5 py-2 ${errorMessage || selectedQueue === null ? 'gray' : 'theme clickable'}`}
          onClick={loadData}
        >
          <span aria-live="polite">{loading ? 'Loading...' : 'Load statistics'}</span>
        </div>
      </div>

      {/* Results */}
      {sessions !== null && (
        <>
          {/* Tabs */}
          <ul className="nav nav-tabs mb-3">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'sessions' ? 'active' : ''}`}
                onClick={() => setActiveTab('sessions')}
              >
                Sessions
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'queue_log' ? 'active' : ''}`}
                onClick={() => { setActiveTab('queue_log'); if (queueLog === null) loadQueueLog(); }}
              >
                Queue Log
              </button>
            </li>
          </ul>

          {/* ═══ Sessions tab ═══ */}
          {activeTab === 'sessions' && (
            <>
              {/* Min duration filter — shown whenever sessions have loaded.
                  NOTE: may no longer be necessary now that the hour-range slider lets
                  teachers narrow down the view; consider removing in a future cleanup. */}
              <div className="mb-3 d-flex align-items-center" style={{ gap: '0.5rem' }}>
                <label className="mb-0 text-muted small">Min session duration:</label>
                <input
                  type="number"
                  min={0}
                  style={{ width: '5rem' }}
                  className="form-control form-control-sm"
                  value={minDurationMin}
                  onChange={e => setMinDurationMin(Number(e.target.value))}
                />
                <span className="text-muted small">min</span>
                {hiddenCount > 0 && (
                  <span className="text-muted small">({hiddenCount} hidden)</span>
                )}
              </div>

              {sessions.length === 0 ? (
                <p>No lab sessions found in the selected time period.</p>
              ) : (
                <>
                  {/* Day summary table */}
                  <div className="table-responsive mb-1">
                    <table className="table table-striped table-hover table-sm">
                      <thead className="thead-dark">
                        <tr>
                          <th
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => setSessionsSort(s => s === 'desc' ? 'asc' : 'desc')}
                          >
                            Date {sessionsSort === 'desc' ? '▼' : '▲'}
                          </th>
                          <th>Helped</th>
                          <th>Presentations</th>
                          <th>Avg Wait</th>
                          <th>Max Wait</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...filteredGroups].sort((a, b) => sessionsSort === 'desc'
                          ? b.date.localeCompare(a.date)
                          : a.date.localeCompare(b.date)
                        ).map(g => (
                          <tr
                            key={g.date}
                            onClick={() => loadDateDetail(g)}
                            style={{
                              cursor: 'pointer',
                              background: selectedDateKey === g.date ? '#d0e8f5' : undefined,
                            }}
                          >
                            <td>{fmtDate(g.date)}</td>
                            <td>{g.total_helped}</td>
                            <td>{g.total_presented}</td>
                            <td>{fmtWait(g.avg_wait_secs)}</td>
                            <td>{fmtWait(g.max_wait_secs)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-muted small mb-3">Click a row to see timeline and hourly breakdown.</p>

                  {/* ── Detail section ── */}
                  {selectedGroup && (
                    <>
                      <div className="section-divider" />
                      <h5 className="mb-2">{fmtDate(selectedGroup.date)}</h5>

                      {/* Hour range filter */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', fontSize: '0.85rem', color: '#495057', marginBottom: '2.2rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0 }}>
                          <span className="text-muted">From</span>
                          <input
                            type="number" min={0} max={23}
                            value={hourFrom}
                            className="form-control"
                            style={{ width: '3.6rem', padding: '0.15rem 0.3rem', fontSize: '0.85rem', height: 'auto' }}
                            onChange={e => setHourFrom(Math.max(0, Math.min(23, Number(e.target.value))))}
                          />
                        </div>
                        <div style={{ flex: 1, padding: '0 0.2rem' }}>
                          <div ref={sliderRef} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0 }}>
                          <span className="text-muted">To</span>
                          <input
                            type="number" min={0} max={23}
                            value={hourTo}
                            className="form-control"
                            style={{ width: '3.6rem', padding: '0.15rem 0.3rem', fontSize: '0.85rem', height: 'auto' }}
                            onChange={e => setHourTo(Math.max(0, Math.min(23, Number(e.target.value))))}
                          />
                        </div>
                      </div>
                      <p className="text-muted small mb-3">
                        Range auto-set to {hourFrom}:00–{hourTo}:59 (first–last event). Adjust to zoom in.
                      </p>

                      {/* Queue load chart */}
                      {sessionDetail === null ? (
                        <p>Loading charts...</p>
                      ) : chartPoints.length === 0 ? (
                        <p>No chart data for this session.</p>
                      ) : (
                        <>
                          <div className="mb-2" style={{ fontSize: '0.85rem' }}>
                            <label style={{ cursor: 'pointer', marginBottom: 0 }}>
                              <input
                                type="checkbox"
                                checked={alignAxes}
                                onChange={e => setAlignAxes(e.target.checked)}
                                style={{ marginRight: '0.4rem' }}
                              />
                              Align x-axes
                            </label>
                          </div>

                          <div className="chart-wrap">
                            <div className="chart-header">
                              <h6 className="mb-0">Queue load</h6>
                              <button className="btn-dl" onClick={() => downloadPng(queueLoadRef, `queue-load-${selectedDateKey}`)}>↓ PNG</button>
                            </div>
                            <div style={{ height: '220px' }}>
                              {(() => {
                                const t0 = new Date(selectedGroup.primary.lab_start).getTime();
                                const hasHelp = chartPoints.some(p => p.help_amount > 0);
                                const hasPresent = chartPoints.some(p => p.present_amount > 0);
                                const mkTime = (p: ChartPoint) => new Date(t0 + p.minutes_from_start * 60_000);
                                const datasets: any[] = [
                                  { label: 'Total', data: chartPoints.map(p => ({ x: mkTime(p), y: p.queue_length })), borderColor: '#adb5bd', backgroundColor: '#adb5bd', pointRadius: 2, tension: 0 },
                                ];
                                if (hasHelp)    datasets.push({ label: 'Help',          data: chartPoints.map(p => ({ x: mkTime(p), y: p.help_amount })),    borderColor: '#4e79a7', backgroundColor: '#4e79a7', pointRadius: 2, tension: 0 });
                                if (hasPresent) datasets.push({ label: 'Presentations', data: chartPoints.map(p => ({ x: mkTime(p), y: p.present_amount })), borderColor: '#f28e2b', backgroundColor: '#f28e2b', pointRadius: 2, tension: 0 });
                                return (
                                  <Line
                                    ref={queueLoadRef}
                                    data={{ datasets }}
                                    options={{
                                      responsive: true, maintainAspectRatio: false,
                                      plugins: { legend: { position: 'bottom' } },
                                      scales: {
                                        x: { type: 'time', ...(sharedAxisRange ?? {}), time: { unit: 'minute', stepSize: 30, displayFormats: { minute: 'HH:mm' } } },
                                        y: { min: 0, title: { display: true, text: 'Students' } },
                                      },
                                    }}
                                  />
                                );
                              })()}
                            </div>
                          </div>

                          {/* 5-min stacked bar */}
                          {stackedBuckets.length > 0 && (
                            <div className="chart-wrap">
                              <div className="chart-header">
                                <h6 className="mb-0">5-minute breakdown</h6>
                                <button className="btn-dl" onClick={() => downloadPng(fiveMinRef, `5min-${selectedDateKey}`)}>↓ PNG</button>
                              </div>
                              <div style={{ height: '200px' }}>
                                <Bar
                                  ref={fiveMinRef}
                                  data={{
                                    datasets: [
                                      { label: 'Assisted', data: stackedBuckets.map(([t, a]) => ({ x: t, y: a })),    backgroundColor: '#59a14f' },
                                      { label: 'Waiting',  data: stackedBuckets.map(([t, , w]) => ({ x: t, y: w })), backgroundColor: '#adb5bd' },
                                    ],
                                  }}
                                  options={{
                                    responsive: true, maintainAspectRatio: false,
                                    plugins: { legend: { position: 'bottom' } },
                                    scales: {
                                      x: { type: 'time', stacked: true, ...(sharedAxisRange ?? {}), time: { unit: 'minute', stepSize: 5, displayFormats: { minute: 'HH:mm' } } },
                                      y: { stacked: true, min: 0, title: { display: true, text: 'Students' } },
                                    },
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {/* ── Hourly breakdown table ── */}
                      {hourlySlots !== null && hourlySlots.length > 0 && (
                        <>
                          <div className="section-divider" />

                          <div className="option-box">
                            <span className="opt-label">Wait time attributed by:</span>
                            <label>
                              <input type="radio" name="bucket" value="service" checked={waitMode === 'service'} onChange={() => setWaitMode('service')} />
                              {' '}<strong>By service</strong> — wait counts in the hour the student was served
                            </label>
                            <label>
                              <input type="radio" name="bucket" value="arrival" checked={waitMode === 'arrival'} onChange={() => setWaitMode('arrival')} />
                              {' '}<strong>By arrival</strong> — wait counts in the hour the student joined
                            </label>
                          </div>

                          <div className="chart-wrap">
                            <div className="chart-header">
                              <h6 className="mb-0">Hourly breakdown</h6>
                              <button className="btn-dl" onClick={downloadHourlyCsv}>↓ CSV</button>
                            </div>
                            <div className="table-responsive mb-1">
                              <table className="table table-sm hour-table">
                                <thead className="thead-light">
                                  <tr>
                                    <th rowSpan={2}>Hour</th>
                                    <th colSpan={2} className="group-h">Queued for</th>
                                    <th colSpan={2} className="group-h">Assisted</th>
                                    <th rowSpan={2}>Avg Wait</th>
                                    <th rowSpan={2}>Max Wait</th>
                                    <th rowSpan={2} title="Queue length at last event in this hour">Remaining</th>
                                  </tr>
                                  <tr>
                                    <th className="sub-h">Help</th>
                                    <th className="sub-h">Present.</th>
                                    <th className="sub-h">Help</th>
                                    <th className="sub-h">Present.</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredHourly.length === 0 ? (
                                    <tr><td colSpan={8} className="text-muted">No hours in selected range.</td></tr>
                                  ) : filteredHourly.map(s => {
                                    const avg = waitMode === 'service' ? s.avg_wait_service_secs : s.avg_wait_arrival_secs;
                                    const max = waitMode === 'service' ? s.max_wait_service_secs : s.max_wait_arrival_secs;
                                    const dim = s.assisted_help === 0 && s.assisted_present === 0 && avg === null;
                                    return (
                                      <tr key={s.hour} className={dim ? 'dim' : ''}>
                                        <td><strong>{s.hour}–{s.hour + 1}</strong></td>
                                        <td>{s.queued_help}</td>
                                        <td>{s.queued_present}</td>
                                        <td>{s.assisted_help}</td>
                                        <td>{s.assisted_present}</td>
                                        <td>{fmtWait(avg)}</td>
                                        <td>{fmtWait(max)}</td>
                                        <td>{s.remaining}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </>
                      )}

                      {/* ── TA activity ── */}
                      {taData.length > 0 && (
                        <>
                          <div className="section-divider" />
                          <div className="chart-wrap">
                            <div className="chart-header">
                              <h6 className="mb-0">TA activity — {fmtDate(selectedGroup.date)}</h6>
                              <button className="btn-dl" onClick={() => downloadPng(taChartRef, `ta-${selectedDateKey}`)}>↓ PNG</button>
                            </div>
                            <div style={{ height: `${taData.length * 40 + 80}px` }}>
                              <Bar
                                ref={taChartRef}
                                data={{
                                  labels: taData.map(ta => ta.username),
                                  datasets: [
                                    { label: 'Helped',        data: taData.map(ta => ta.helped),    backgroundColor: '#4e79a7' },
                                    { label: 'Presentations', data: taData.map(ta => ta.presented), backgroundColor: '#f28e2b' },
                                  ],
                                }}
                                options={{
                                  indexAxis: 'y',
                                  responsive: true,
                                  maintainAspectRatio: false,
                                  plugins: { legend: { position: 'bottom' } },
                                  scales: {
                                    x: { stacked: true, min: 0, title: { display: true, text: 'Students' } },
                                    y: { stacked: true },
                                  },
                                }}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}

          {/* ═══ Queue Log tab ═══ */}
          {activeTab === 'queue_log' && (
            <div className="mb-4">
              {queueLog === null ? (
                <p>Loading queue log...</p>
              ) : (
                <>
                  <div style={{ position: 'relative' }}>
                    <pre style={{ overflowY: 'scroll', overflowX: 'scroll', maxHeight: '40vh' }}>
                      <code>{queueLog}</code>
                    </pre>
                    <div className="row" style={{ position: 'absolute', right: '1%', top: '4%' }}>
                      <div
                        className="clickable card p-2 mr-2"
                        style={{ backgroundColor: 'rgba(0,0,0,0.1)' }}
                        onClick={() => { copyToClipboard(queueLog); addFlashCard('Copied to clipboard'); }}
                        title="Copy to clipboard"
                      >
                        <Copy />
                      </div>
                      <div
                        className="clickable card p-2"
                        style={{ backgroundColor: 'rgba(0,0,0,0.1)' }}
                        onClick={() => downloadFile(`${selectedQueue}-queue-log.json`, queueLog)}
                        title="Download"
                      >
                        <Download />
                      </div>
                    </div>
                  </div>
                  <p className="text-muted small mt-2">
                    This data is also available via the REST API — see the <Link to="/ApiKeys">API</Link> tab.
                  </p>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
